import { ProductShot } from "@/components/marketing/MediaFallback";

// Reusable hero visual framing — a product dashboard shown in a laptop frame, a
// mobile app in a phone frame, or a brand/AI image in an image card. Brand 2.2:
// no glow behind them (aurora is reserved for AI provenance); frames use the
// ink tokens.

export type DeviceMockVariant = "laptop" | "phone" | "card";

/** Intrinsic pixel size of a screenshot, used to shape the phone screen. */
export type DeviceMockScreen = { width: number; height: number };

type DeviceMockBaseProps = {
  variant: DeviceMockVariant;
  src: string;
  alt: string;
  eager?: boolean;
  className?: string;
  // Card variant: how the image fills the 16:9 frame. Defaults to "cover" so
  // images fill edge-to-edge (no dark surround — the "platform" look). Pass
  // "contain" for renders that must be shown whole (e.g. the flywheel diagram).
  imageFit?: "contain" | "cover";
  // CSS object-position for the cover crop, e.g. "50% 22%" to bias toward a
  // face near the top. Defaults to centre.
  objectPosition?: string;
  // Card variant aspect ratio. Defaults to "video" (16:9). "square" (1:1) suits
  // a portrait photo shown small/de-emphasised (e.g. the About founder photo).
  cardAspect?: "video" | "square";
};

// `fit="height"` makes the phone size itself to the parent's height (the bezel
// becomes `h-full w-fit`) so a tall portrait screenshot stays fully visible
// inside a short, fixed-aspect container (e.g. the demo-hub card). Default
// (width-driven) suits the tall hero column, where the image sets its own
// height and nothing has to be shaped in advance.
//
// That mode needs `screen`, the screenshot's intrinsic size, and the type makes
// it mandatory rather than defaulting: the screen box has to be shaped before
// the image loads (`w-fit` collapses otherwise), so a wrong or assumed ratio is
// silently baked into the layout with no runtime signal. Two things follow from
// where the ratio goes:
//   - It sits on the inner screen, never on the bordered bezel. The border is a
//     fixed 9px, so folding it into the ratio squeezes the screen by an amount
//     that grows as the phone shrinks (~5% of the source width in a desktop
//     card, ~19% at a 320px viewport) and `object-cover` crops exactly that off
//     both edges.
//   - It is an inline style, not a Tailwind arbitrary value. `aspect-[w/h]`
//     built from props cannot be generated — the JIT only sees literals in
//     source — so a dynamic class would silently produce no ratio at all.
//
// Only the phone branch reads either prop, so the height-fit member pins
// `variant` to "phone" as well. Otherwise a laptop or card could be handed a
// `fit`/`screen` pair that renders nothing — an accepted no-op is exactly the
// silent-wrong-ratio failure this type exists to prevent.
type DeviceMockPhoneFitProps =
  | { variant: "phone"; fit: "height"; screen: DeviceMockScreen }
  | { fit?: never; screen?: never };

type DeviceMockProps = DeviceMockBaseProps & DeviceMockPhoneFitProps;

/**
 * Renders a screenshot inside a laptop, phone, or card frame on a shared aurora
 * glow, so product visuals read as one design language across the site.
 *
 * @param props.variant Which frame to draw. "phone" is the only variant that
 *   reads `fit` and `screen`.
 * @param props.fit Pass "height" to size the phone to its parent's height
 *   instead of its own width — for a portrait screenshot in a short container.
 *   Requires `screen`, and requires `variant: "phone"`.
 * @param props.screen The screenshot's intrinsic pixel size, which shapes the
 *   phone screen before the image loads. Must match the asset: the ratio is
 *   applied as an inline style with no runtime check, so a wrong value crops.
 */
// Props are read off `props` rather than destructured so TypeScript keeps the
// fit/screen correlation: `props.fit === "height"` narrows `props.screen` to a
// present value, which destructuring would discard.
export function DeviceMock(props: DeviceMockProps) {
  const {
    variant,
    src,
    alt,
    eager,
    className,
    imageFit,
    objectPosition,
    cardAspect,
  } = props;
  const loading = eager ? "eager" : "lazy";

  if (variant === "phone") {
    const heightFit = props.fit === "height";
    const screenStyle =
      props.fit === "height"
        ? { aspectRatio: `${props.screen.width} / ${props.screen.height}` }
        : undefined;
    return (
      <div
        className={`relative flex justify-center ${heightFit ? "h-full" : ""} ${className ?? ""}`}
      >
        <div
          className={`rounded-[2.4rem] border-[9px] border-caire-ink bg-ink text-ink-fg shadow-[0_40px_90px_-30px_hsl(var(--cb-ink-hsl)/0.6)] ${heightFit ? "h-full w-fit" : "w-[clamp(190px,62%,260px)]"}`}
        >
          <div
            className="relative h-full overflow-hidden rounded-[1.6rem]"
            style={screenStyle}
          >
            <div className="absolute left-1/2 top-1.5 z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-ink" />
            <ProductShot
              src={src}
              alt={alt}
              loading={loading}
              className={
                heightFit
                  ? "block h-full w-full object-cover object-top"
                  : "block aspect-phone w-full"
              }
            />
          </div>
        </div>
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div className={`relative ${className ?? ""}`}>
        <div
          className={`${cardAspect === "square" ? "aspect-square" : "aspect-video"} overflow-hidden`}
        >
          <ProductShot
            src={src}
            alt={alt}
            loading={loading}
            className={`block h-full w-full ${
              imageFit === "contain" ? "object-contain" : "object-cover"
            }`}
            style={
              imageFit === "contain"
                ? undefined
                : { objectPosition: objectPosition ?? "50% 50%" }
            }
          />
        </div>
      </div>
    );
  }

  // laptop
  return (
    <div className={`relative ${className ?? ""}`}>
      <div className="[perspective:1900px]">
        <div className="[transform:rotateX(3deg)] transition-transform duration-700 hover:[transform:rotateX(0deg)] motion-reduce:[transform:none]">
          <div className="relative overflow-hidden rounded-t-[1rem] border-[8px] border-b-[10px] border-caire-ink bg-caire-ink shadow-[0_40px_90px_-34px_hsl(var(--cb-ink-hsl)/0.6)]">
            <div
              aria-hidden="true"
              className="absolute left-1/2 top-1.5 z-10 h-1 w-1 -translate-x-1/2 rounded-full bg-caire-muted"
            />
            <ProductShot
              src={src}
              alt={alt}
              loading={loading}
              className="block aspect-video w-full rounded-[0.35rem] object-cover object-top"
            />
          </div>
          <div className="relative mx-[-2%] h-5 rounded-b-[1.2rem] bg-[linear-gradient(180deg,hsl(var(--cb-slate-hsl))_0%,hsl(var(--cb-ink-hsl))_100%)] shadow-[0_18px_30px_-20px_hsl(var(--cb-ink-hsl)/0.7)]">
            <div className="absolute left-1/2 top-0 h-1.5 w-24 -translate-x-1/2 rounded-b-md bg-caire-ink/85" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeviceMock;
